/** Pure toast queue rules, kept free of React and the DOM so they can be unit tested. */

export type ToastTone = 'success' | 'error' | 'info'

export type ToastItem = {
  id: string
  tone: ToastTone
  title: string
  description?: string
}

/** At most three toasts show at once. */
export const MAX_TOASTS = 3

/** Success and info toasts close after four seconds. */
export const AUTO_DISMISS_MS = 4000

/** Errors stay until the person dismisses them, so they return null. */
export function autoDismissDelay(tone: ToastTone): number | null {
  return tone === 'error' ? null : AUTO_DISMISS_MS
}

/** Errors are announced assertively; everything else politely. */
export function livePoliteness(tone: ToastTone): 'assertive' | 'polite' {
  return tone === 'error' ? 'assertive' : 'polite'
}

/**
 * Adds a toast to the end of the queue. When the queue is over the limit, the
 * oldest success or info toast goes first, because errors must stay until
 * dismissed. Only when every toast is an error does the oldest error go.
 */
export function addToast(queue: readonly ToastItem[], item: ToastItem, max = MAX_TOASTS): ToastItem[] {
  const next = [...queue, item]
  while (next.length > max) {
    const oldestTransient = next.findIndex((toast) => toast.tone !== 'error' && toast.id !== item.id)
    next.splice(oldestTransient === -1 ? 0 : oldestTransient, 1)
  }
  return next
}

export function removeToast(queue: readonly ToastItem[], id: string): ToastItem[] {
  return queue.filter((toast) => toast.id !== id)
}
